import {
  ChatCustomBlockTypes,
  FsmDefMetaTypes,
  type ChatMessage,
  type Metadata,
} from "@do/shared-types";

const isBotMessage = (message: ChatMessage) => !message.user && !message.advisor;

export const isErrorMessage = (message: ChatMessage) => {
  const metadata = message.blocks?.find(
    (block) => block.type === ChatCustomBlockTypes.text
  )?.metadata;

  return Array.isArray(metadata) && metadata.some((meta) => Boolean(meta?.["error"]));
};

export const hasStepsFormBlock = (message: ChatMessage) =>
  message.blocks?.some((block) => {
    if (block.type !== ChatCustomBlockTypes.steps) return false;

    const initialState = block.fsmDef?.states?.[block.fsmDef.initial];

    return initialState?.meta?.type === FsmDefMetaTypes.form;
  }) ?? false;

export const hasFormSuccessBlock = (message: ChatMessage) =>
  message.blocks?.some((block) => block.type === ChatCustomBlockTypes.formSuccess) ?? false;

const mergeMetadata = (a: ChatMessage["metadata"], b: ChatMessage["metadata"]) => {
  if (Array.isArray(a) && Array.isArray(b)) return [...a, ...b] as Metadata[];
  return a ?? b;
};

const mergeUsedSourcesMetadatas = (
  a: ChatMessage["used_sources_metadatas"],
  b: ChatMessage["used_sources_metadatas"]
) => {
  if (Array.isArray(a) && Array.isArray(b)) return [...a, ...b];
  return b ?? a;
};

/**
 * Merge consecutive bot messages into a single message so UI actions (copy/tts/edit)
 * are displayed only once for a group of bot texts.
 *
 * Messages considered "error" are never merged.
 */
export const mergeBotMessages = (messages: ChatMessage[]) => {
  const merged: ChatMessage[] = [];
  let current: ChatMessage | null = null;

  const flush = () => {
    if (!current) return;
    merged.push(current);
    current = null;
  };

  for (const message of messages) {
    const isMergeableBot =
      isBotMessage(message) &&
      !isErrorMessage(message) &&
      !hasStepsFormBlock(message) &&
      !hasFormSuccessBlock(message) &&
      message.id !== "streamed-msg";

    if (!isMergeableBot) {
      flush();
      merged.push(message);
      continue;
    }

    if (!current) {
      current = {
        ...message,
        blocks: message.blocks ? [...message.blocks] : [],
      };
      continue;
    }

    current = {
      ...current,
      blocks: [...(current.blocks ?? []), ...(message.blocks ?? [])],
      // Preserve disabledInput (etc.) from the last message in the group when present
      events: message.events ?? current.events,
      metadata: mergeMetadata(current.metadata, message.metadata),
      used_sources_metadatas: mergeUsedSourcesMetadatas(
        current.used_sources_metadatas,
        message.used_sources_metadatas
      ),
    };
  }

  flush();
  return merged;
};
